import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useLang } from "@/contexts/LangContext";
import { ArrowRight } from "lucide-react";

const HERO_BG =
  "https://images.pexels.com/photos/18709769/pexels-photo-18709769.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.3]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative h-screen min-h-[680px] w-full overflow-hidden bg-black"
    >
      <motion.div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})`, y, opacity }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/30 to-black/75" />

      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-24 md:pb-32">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-6"
        >
          ✦ {t.hero.eyebrow}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="font-heading text-5xl md:text-6xl lg:text-[5.5rem] leading-[1.05] tracking-tight text-white max-w-4xl whitespace-pre-line"
        >
          {t.hero.title}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-8 text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed"
        >
          {t.hero.subtitle}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <button
            data-testid="hero-explore-button"
            onClick={() => scrollTo("destinations")}
            className="group bg-[#C62828] hover:bg-[#B71C1C] text-white px-8 py-4 uppercase tracking-wider text-sm font-semibold transition-all duration-300 inline-flex items-center gap-3"
          >
            {t.hero.cta_primary}
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            data-testid="hero-plan-button"
            onClick={() => scrollTo("itinerary")}
            className="border border-white/40 text-white hover:bg-white hover:text-[#121212] px-8 py-4 uppercase tracking-wider text-sm font-semibold transition-all duration-300"
          >
            {t.hero.cta_secondary}
          </button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-white/60 text-xs uppercase tracking-[0.3em]"
      >
        scroll
      </motion.div>
    </section>
  );
};

export default Hero;
